import React, { useMemo } from 'react';
import { Plus, BarChart3 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../lib/api';
import { useChartConfigs } from '../../../../hooks/useChartConfigs';
import ChartCard from '../questions/ChartCard';
import Button from '../../../Button';
import { getChartableFields, buildDemographicQuestions, Question } from '../../../../utils/chartableFields';
import { aggregateResponsesByField } from '../../../../utils/analyticsUtils';
import type { ReportFormState } from './OutreachReportWizard';

interface Props {
  itemId: string;
  viewId: string;
  formState: ReportFormState;
}

const ReportStep3Charts: React.FC<Props> = ({ itemId, viewId }) => {
  const {
    charts,
    addChart,
    updateChart,
    deleteChart,
    isLoading: chartsLoading
  } = useChartConfigs(itemId, viewId);

  // Fetch item definition for questions
  const { data: itemData } = useQuery(
    ['outreach-item-def', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}`);
      return res.data.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // Fetch responses for data preview
  const { data: analyticsData } = useQuery(
    ['outreach-analytics-data', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}/analytics-data?include=responses`);
      return res.data.data;
    },
    { staleTime: 60 * 1000 }
  );

  const questions: Question[] = useMemo(() => {
    const raw = itemData?.definition_json?.questions;
    const defined = !raw || !Array.isArray(raw) ? [] : raw.map((q: any, idx: number) => {
      const text = q.question || q.label || q.text || q.title || '';
      return {
        id: q.id || `question_${idx}`,
        label: text ? `Q${idx + 1}: ${text}` : `Question ${idx + 1}`,
        type: q.type || 'text',
        options: q.options || []
      };
    });
    return [...defined, ...buildDemographicQuestions(itemData?.definition_json)];
  }, [itemData]);
  const responses = analyticsData?.responses?.data || analyticsData?.responses || [];
  const chartableFields = useMemo(() => getChartableFields(questions, { includeAll: true }), [questions]);

  const handleAddChart = () => {
    const defaultField = chartableFields[0];
    if (!defaultField) return;

    addChart({
      question_id: defaultField.id,
      chart_type: defaultField.suggestedChartTypes[0] || 'bar',
      title: defaultField.label
    });
  };

  const getChartData = (questionId: string) => {
    const field = chartableFields.find(f => f.id === questionId);
    return aggregateResponsesByField(responses, questionId, field?.options);
  };

  if (chartsLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-8 bg-white dark:bg-background-dark-secondary rounded w-40 mb-4" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-64 bg-white dark:bg-background-dark-secondary rounded" />
          <div className="h-64 bg-white dark:bg-background-dark-secondary rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Report Charts
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
            Add charts to this report. These are independent of the charts on the Results Quick View tab.
          </p>
        </div>
        <Button
          onClick={handleAddChart}
          size="sm"
          disabled={chartableFields.length === 0}
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Chart
        </Button>
      </div>

      {chartableFields.length === 0 ? (
        <div className="card text-center py-8 text-text-secondary dark:text-text-dark-secondary">
          No chartable fields found. Charts work best with rating, select, or checkbox questions.
        </div>
      ) : charts.length === 0 ? (
        <div className="card text-center py-8 text-text-secondary dark:text-text-dark-secondary">
          No charts added to this report yet. Click &quot;Add Chart&quot; to create your first visualization.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {charts.map((chart) => (
            <ChartCard
              key={chart.id}
              chart={chart}
              chartableFields={chartableFields}
              data={getChartData(chart.question_id)}
              responses={responses}
              onUpdate={(updates) => updateChart(chart.id, updates)}
              onDelete={() => deleteChart(chart.id)}
              hideDownload
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportStep3Charts;
